import { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import Navbar from '@/components/Navbar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Utensils, Heart, Lightbulb, TrendingUp, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const Dashboard = () => {
  const { user, API } = useAuth();
  const [profile, setProfile] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [mealPlans, setMealPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [profileRes, metricsRes, plansRes] = await Promise.all([
        axios.get(`${API}/users/profile`),
        axios.get(`${API}/health/metrics`),
        axios.get(`${API}/meal-plans`),
      ]);

      setProfile(profileRes.data.profile);
      setMetrics(metricsRes.data.metrics || []);
      setMealPlans(plansRes.data || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const quickStats = [
    {
      title: 'Planuri de Mese',
      value: mealPlans.length,
      icon: Utensils,
      link: '/meal-plans',
      color: '#1A4D2E',
    },
    {
      title: 'Metrici Sănătate',
      value: metrics.length,
      icon: Heart,
      link: '/health',
      color: '#9BCF53',
    },
    {
      title: 'Recomandări AI',
      value: '1',
      icon: Lightbulb,
      link: '/recommendations',
      color: '#1A4D2E',
    },
  ];

  const latestMetric = metrics[0];

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      <Navbar />

      <div className="container mx-auto px-4 md:px-6 lg:px-12 py-6 md:py-8 max-w-7xl" data-testid="dashboard">
        {/* Welcome Section */}
        <div className="mb-6 md:mb-8 animate-fade-in">
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light text-[#1A4D2E] mb-2">
            Bun venit, {user?.email?.split('@')[0]}!
          </h1>
          <p className="text-[#4a5568] text-base md:text-lg">
            Acesta este tabloul tău de bord pentru sănătate și nutriție
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {quickStats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div
                key={index}
                className="animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <Link to={stat.link}>
                  <Card
                    className="hover:shadow-lg transition-all duration-300 cursor-pointer border-none"
                    data-testid={`stat-card-${index}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-[#4a5568] text-sm mb-1">{stat.title}</p>
                          <p className="text-3xl font-bold text-[#1A4D2E]">{stat.value}</p>
                        </div>
                        <div
                          className="w-14 h-14 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: `${stat.color}20` }}
                        >
                          <Icon className="w-7 h-7" style={{ color: stat.color }} />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </div>
            );
          })}
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Latest Health Metrics */}
          <div className="animate-fade-in" style={{ animationDelay: '300ms' }}>
            <Card className="border-none card-shadow">
              <CardHeader>
                <CardTitle className="font-serif text-2xl text-[#1A4D2E] flex items-center justify-between">
                  <span>Ultimele Metrici</span>
                  <Link to="/health">
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid="view-all-metrics-button"
                      className="border-[#1A4D2E]/20 text-[#1A4D2E] hover:bg-[#F5F5F0] rounded-full"
                    >
                      Vezi Toate
                    </Button>
                  </Link>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {latestMetric ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-[#F5F5F0] rounded-xl">
                      <span className="text-[#4a5568]">Glicemie</span>
                      <span className="text-[#1A4D2E] font-semibold">
                        {latestMetric.glucose || 'N/A'} mg/dL
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-[#F5F5F0] rounded-xl">
                      <span className="text-[#4a5568]">Colesterol</span>
                      <span className="text-[#1A4D2E] font-semibold">
                        {latestMetric.cholesterol || 'N/A'} mg/dL
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-[#F5F5F0] rounded-xl">
                      <span className="text-[#4a5568]">Tensiune</span>
                      <span className="text-[#1A4D2E] font-semibold">
                        {latestMetric.blood_pressure_systolic || 'N/A'}/
                        {latestMetric.blood_pressure_diastolic || 'N/A'} mmHg
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-[#4a5568] mb-4">Nu ai adăugat încă metrici de sănătate</p>
                    <Link to="/health">
                      <Button
                        data-testid="add-first-metric-button"
                        className="bg-[#1A4D2E] text-white rounded-full hover:bg-[#143d24]"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Adăugă Prima Metrică
                      </Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Recent Meal Plans */}
          <div className="animate-fade-in" style={{ animationDelay: '400ms' }}>
            <Card className="border-none card-shadow">
              <CardHeader>
                <CardTitle className="font-serif text-2xl text-[#1A4D2E] flex items-center justify-between">
                  <span>Planuri de Mese</span>
                  <Link to="/meal-plans">
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid="view-all-meal-plans-button"
                      className="border-[#1A4D2E]/20 text-[#1A4D2E] hover:bg-[#F5F5F0] rounded-full"
                    >
                      Vezi Toate
                    </Button>
                  </Link>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {mealPlans.length > 0 ? (
                  <div className="space-y-3">
                    {mealPlans.slice(0, 3).map((plan) => (
                      <Link key={plan.id} to={`/meal-plans`}>
                        <div className="p-4 bg-[#F5F5F0] rounded-xl hover:bg-[#E2E8F0] transition-colors cursor-pointer">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-[#1A4D2E] font-medium">
                                Plan {plan.days} zile
                              </p>
                              <p className="text-sm text-[#4a5568]">
                                {new Date(plan.created_at).toLocaleDateString('ro-RO')}
                              </p>
                            </div>
                            <Utensils className="w-5 h-5 text-[#9BCF53]" />
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-[#4a5568] mb-4">Nu ai generat încă planuri de mese</p>
                    <Link to="/meal-plans">
                      <Button
                        data-testid="generate-first-meal-plan-button"
                        className="bg-[#1A4D2E] text-white rounded-full hover:bg-[#143d24]"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Generează Primul Plan
                      </Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 animate-fade-in" style={{ animationDelay: '500ms' }}>
          <Card className="border-none card-shadow bg-gradient-to-br from-[#1A4D2E] to-[#143d24] text-white">
            <CardContent className="p-8">
              <h3 className="font-serif text-2xl mb-4">Acțiuni Rapide</h3>
              <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
                <Link to="/meal-plans">
                  <Button
                    variant="secondary"
                    data-testid="quick-action-meal-plan"
                    className="w-full bg-white/10 hover:bg-white/20 text-white border-none rounded-full"
                  >
                    <Utensils className="w-4 h-4 mr-2" />
                    Plan Nou
                  </Button>
                </Link>
                <Link to="/health">
                  <Button
                    variant="secondary"
                    data-testid="quick-action-add-metric"
                    className="w-full bg-white/10 hover:bg-white/20 text-white border-none rounded-full"
                  >
                    <Heart className="w-4 h-4 mr-2" />
                    Adăugă Metrică
                  </Button>
                </Link>
                <Link to="/recommendations">
                  <Button
                    variant="secondary"
                    data-testid="quick-action-recommendations"
                    className="w-full bg-white/10 hover:bg-white/20 text-white border-none rounded-full"
                  >
                    <Lightbulb className="w-4 h-4 mr-2" />
                    Recomandări
                  </Button>
                </Link>
                <Link to="/profile">
                  <Button
                    variant="secondary"
                    data-testid="quick-action-profile"
                    className="w-full bg-white/10 hover:bg-white/20 text-white border-none rounded-full"
                  >
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Profil
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
